import React, {useMemo, useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import {getAll, createOne, patchOne, deleteOne} from '../../../services/common_requests'
import {useQueryResponse, ResponseData} from './core/QueryResponseProvider'
import {KTCard, KTSVG} from '../../../../_dashboard/helpers'
import {NewslettersTable} from './table/NewslettersTable'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import dataAxios from '../../../modules/auth/core/dataAxios'
import {Newsletter} from './core/_models'

const DUPLICATE_EMAIL_CODE = 4104
const API_ROOT = process.env.REACT_APP_API_URL || ''

type ListFilters = {
  page: number
  limit: number
  subscription: 'all' | 'subscribed' | 'unsubscribed'
  q: string
  dateFrom: string
  dateTo: string
  sort?: string
  order?: 'asc' | 'desc'
}

const NewslettersList: React.FC = () => {
  const intl = useIntl()
  const {updateState} = useQueryResponse()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<ListFilters>({
    page: 0,
    limit: 10,
    subscription: 'all',
    q: '',
    dateFrom: '',
    dateTo: '',
  })
  const [showAdd, setShowAdd] = useState(false)
  const [editRow, setEditRow] = useState<Newsletter | null>(null)
  const [deleteRow, setDeleteRow] = useState<Newsletter | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const [addEmail, setAddEmail] = useState('')
  const [addName, setAddName] = useState('')
  const [addGdpr, setAddGdpr] = useState(false)

  const [editName, setEditName] = useState('')
  const [editSubscribed, setEditSubscribed] = useState(true)

  const queryParams = useMemo(() => {
    const p: Record<string, string | number> = {
      page: filters.page,
      size: filters.limit,
    }
    if (filters.subscription !== 'all') {
      p.subscription = filters.subscription
    }
    const q = filters.q.trim()
    if (q) p.q = q
    if (filters.dateFrom) p.dateFrom = filters.dateFrom
    if (filters.dateTo) p.dateTo = filters.dateTo
    if (filters.sort && filters.order) {
      p.sort = filters.order === 'desc' ? `-${filters.sort}` : filters.sort
    }
    return p
  }, [filters])

  const {isFetching} = useQuery<ResponseData>(
    ['newsletters', queryParams],
    () => getAll(queryParams, '/newsletter-emails'),
    {onSuccess: (data) => updateState(data), keepPreviousData: true},
  )

  const invalidate = () => queryClient.invalidateQueries(['newsletters'])

  const handleExport = async () => {
    try {
      setExporting(true)
      const res = await dataAxios.get(`${API_ROOT}/newsletter-emails/export`, {
        responseType: 'blob',
        params: {
          subscription: filters.subscription !== 'all' ? filters.subscription : undefined,
          q: filters.q.trim() || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          sort:
            filters.sort && filters.order
              ? filters.order === 'desc'
                ? `-${filters.sort}`
                : filters.sort
              : undefined,
        },
      })
      const blob = new Blob([res.data], {type: 'text/csv;charset=utf-8;'})
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'newsletter-subscribers.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success(intl.formatMessage({id: 'NEWSLETTER.EXPORT_OK'}))
    } catch {
      toast.error(intl.formatMessage({id: 'NEWSLETTER.EXPORT_FAIL'}))
    } finally {
      setExporting(false)
    }
  }

  const submitAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const email = addEmail.trim()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toast.error(intl.formatMessage({id: 'NEWSLETTER.VALIDATION.EMAIL'}))
      return
    }
    if (!addGdpr) {
      toast.error(intl.formatMessage({id: 'NEWSLETTER.VALIDATION.GDPR'}))
      return
    }
    try {
      setBusyId('new')
      const res: any = await createOne(
        {email, name: addName.trim() || undefined, gdprConsent: true, source: 'admin_manual'},
        '/newsletter-emails',
      )
      if (res?.payload?.code === DUPLICATE_EMAIL_CODE) {
        toast.error(intl.formatMessage({id: 'NEWSLETTER.DUPLICATE_EMAIL'}))
        return
      }
      toast.success(intl.formatMessage({id: 'NEWSLETTER.CREATED_OK'}))
      setShowAdd(false)
      setAddEmail('')
      setAddName('')
      setAddGdpr(false)
      await invalidate()
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage({id: 'NEWSLETTER.SAVE_FAIL'}))
    } finally {
      setBusyId(null)
    }
  }

  const openEdit = (row: Newsletter) => {
    setEditRow(row)
    setEditName(row.name || '')
    setEditSubscribed(!!row.isSubscribed)
  }

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editRow) return
    try {
      setBusyId(String(editRow._id))
      const res: any = await patchOne(
        {name: editName.trim() || undefined, isSubscribed: editSubscribed},
        `/newsletter-emails/${editRow._id}`,
      )
      if (res?.payload?.code === DUPLICATE_EMAIL_CODE) {
        toast.error(intl.formatMessage({id: 'NEWSLETTER.DUPLICATE_EMAIL'}))
        return
      }
      toast.success(intl.formatMessage({id: 'NEWSLETTER.UPDATED_OK'}))
      setEditRow(null)
      await invalidate()
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage({id: 'NEWSLETTER.SAVE_FAIL'}))
    } finally {
      setBusyId(null)
    }
  }

  const confirmDelete = async () => {
    if (!deleteRow) return
    try {
      setBusyId(String(deleteRow._id))
      await deleteOne(deleteRow._id, '/newsletter-emails')
      toast.success(intl.formatMessage({id: 'NEWSLETTER.DELETED_OK'}))
      setDeleteRow(null)
      await invalidate()
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage({id: 'NEWSLETTER.DELETE_FAIL'}))
    } finally {
      setBusyId(null)
    }
  }

  const toggleSubscribe = async (row: Newsletter) => {
    try {
      setBusyId(String(row._id))
      await patchOne({isSubscribed: !row.isSubscribed}, `/newsletter-emails/${row._id}`)
      toast.success(intl.formatMessage({id: 'NEWSLETTER.UPDATED_OK'}))
      await invalidate()
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage({id: 'NEWSLETTER.SAVE_FAIL'}))
    } finally {
      setBusyId(null)
    }
  }

  const setTableFilters = (updates: Partial<ListFilters> | ((prev: ListFilters) => ListFilters)) => {
    if (typeof updates === 'function') {
      setFilters(updates)
      return
    }
    setFilters((prev) => ({
      ...prev,
      ...updates,
      page: updates.page ?? prev.page,
      limit: updates.limit ?? prev.limit,
    }))
  }

  return (
    <>
      <KTCard>
        {/* ── Card Header: filters + actions ── */}
        <div className='card-header border-0 pt-6 pb-4'>
          {/* Filters */}
          <div className='bo-filters d-flex flex-wrap gap-3 align-items-center flex-grow-1'>
            {/* Search */}
            <div className='position-relative d-flex align-items-center'>
              <KTSVG
                path='/media/icons/duotune/general/gen021.svg'
                className='svg-icon-2 position-absolute ms-4'
              />
              <input
                type='search'
                className='form-control form-control-solid w-200px w-lg-250px ps-12'
                placeholder={intl.formatMessage({id: 'NEWSLETTER.FILTER.SEARCH_PLACEHOLDER'})}
                value={filters.q}
                onChange={(e) => setFilters((f) => ({...f, q: e.target.value, page: 0}))}
              />
            </div>

            {/* Status */}
            <select
              className='form-select form-select-solid w-auto min-w-125px'
              value={filters.subscription}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  subscription: e.target.value as ListFilters['subscription'],
                  page: 0,
                }))
              }
            >
              <option value='all'>{intl.formatMessage({id: 'NEWSLETTER.FILTER.ALL'})}</option>
              <option value='subscribed'>
                {intl.formatMessage({id: 'NEWSLETTER.FILTER.SUBSCRIBED'})}
              </option>
              <option value='unsubscribed'>
                {intl.formatMessage({id: 'NEWSLETTER.FILTER.UNSUBSCRIBED'})}
              </option>
            </select>

            {/* Date From */}
            <div className='d-flex align-items-center gap-2'>
              <label className='form-label mb-0 text-muted fs-7 text-nowrap'>
                {intl.formatMessage({id: 'NEWSLETTER.FILTER.DATE_FROM'})}
              </label>
              <input
                type='date'
                className='form-control form-control-solid w-140px'
                value={filters.dateFrom}
                onChange={(e) => setFilters((f) => ({...f, dateFrom: e.target.value, page: 0}))}
              />
            </div>

            {/* Date To */}
            <div className='d-flex align-items-center gap-2'>
              <label className='form-label mb-0 text-muted fs-7 text-nowrap'>
                {intl.formatMessage({id: 'NEWSLETTER.FILTER.DATE_TO'})}
              </label>
              <input
                type='date'
                className='form-control form-control-solid w-140px'
                value={filters.dateTo}
                onChange={(e) => setFilters((f) => ({...f, dateTo: e.target.value, page: 0}))}
              />
            </div>

            {isFetching && (
              <span
                className='spinner-border spinner-border-sm text-primary align-self-center'
                role='status'
              />
            )}
          </div>

          {/* Actions */}
          <div className='card-toolbar d-flex gap-2 ms-3'>
            <button
              type='button'
              className='btn btn-light-primary btn-sm'
              disabled={exporting}
              onClick={() => void handleExport()}
            >
              <KTSVG
                path='/media/icons/duotune/files/fil021.svg'
                className='svg-icon-3 me-1'
              />
              {exporting
                ? intl.formatMessage({id: 'NEWSLETTER.EXPORTING'})
                : intl.formatMessage({id: 'NEWSLETTER.TOOLBAR.EXPORT'})}
            </button>
            <button
              type='button'
              className='btn btn-primary btn-sm'
              onClick={() => setShowAdd(true)}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3 me-1' />
              {intl.formatMessage({id: 'NEWSLETTER.TOOLBAR.ADD'})}
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <NewslettersTable
          setFilters={setTableFilters}
          filters={filters}
          onEdit={openEdit}
          onDelete={setDeleteRow}
          onToggleSubscribe={toggleSubscribe}
          busyId={busyId}
        />
      </KTCard>

      {/* ── Add Modal ── */}
      <Modal show={showAdd} onHide={() => !busyId && setShowAdd(false)} centered>
        <form onSubmit={submitAdd}>
          <Modal.Header closeButton className='border-0 pb-0'>
            <Modal.Title className='fw-bold fs-4'>
              {intl.formatMessage({id: 'NEWSLETTER.MODAL.ADD_TITLE'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='pt-4'>
            <div className='mb-4'>
              <label className='form-label required'>
                {intl.formatMessage({id: 'NEWSLETTER.TABLE.EMAIL'})}
              </label>
              <input
                type='email'
                required
                className='form-control form-control-solid'
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
              />
            </div>
            <div className='mb-4'>
              <label className='form-label'>
                {intl.formatMessage({id: 'NEWSLETTER.TABLE.NAME'})}
              </label>
              <input
                className='form-control form-control-solid'
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
              />
            </div>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='add-gdpr'
                checked={addGdpr}
                onChange={(e) => setAddGdpr(e.target.checked)}
              />
              <label className='form-check-label' htmlFor='add-gdpr'>
                {intl.formatMessage({id: 'NEWSLETTER.MODAL.GDPR_LABEL'})}
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer className='border-0 pt-0'>
            <button
              type='button'
              className='btn btn-light'
              onClick={() => setShowAdd(false)}
              disabled={!!busyId}
            >
              {intl.formatMessage({id: 'NEWSLETTER.MODAL.CANCEL'})}
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={busyId === 'new' || !addGdpr}
            >
              {intl.formatMessage({id: 'NEWSLETTER.MODAL.SAVE'})}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal show={!!editRow} onHide={() => !busyId && setEditRow(null)} centered>
        <form onSubmit={submitEdit}>
          <Modal.Header closeButton className='border-0 pb-0'>
            <Modal.Title className='fw-bold fs-4'>
              {intl.formatMessage({id: 'NEWSLETTER.MODAL.EDIT_TITLE'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='pt-4'>
            <div className='mb-4'>
              <label className='form-label'>
                {intl.formatMessage({id: 'NEWSLETTER.TABLE.EMAIL'})}
              </label>
              <input
                type='email'
                readOnly
                className='form-control form-control-solid bg-light'
                value={editRow?.email || ''}
              />
            </div>
            <div className='mb-4'>
              <label className='form-label'>
                {intl.formatMessage({id: 'NEWSLETTER.TABLE.NAME'})}
              </label>
              <input
                className='form-control form-control-solid'
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                role='switch'
                id='edit-subscribed'
                checked={editSubscribed}
                onChange={(e) => setEditSubscribed(e.target.checked)}
              />
              <label className='form-check-label' htmlFor='edit-subscribed'>
                {intl.formatMessage({id: 'NEWSLETTER.MODAL.SUBSCRIBED_LABEL'})}
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer className='border-0 pt-0'>
            <button
              type='button'
              className='btn btn-light'
              onClick={() => setEditRow(null)}
              disabled={!!busyId}
            >
              {intl.formatMessage({id: 'NEWSLETTER.MODAL.CANCEL'})}
            </button>
            <button type='submit' className='btn btn-primary' disabled={!!busyId}>
              {intl.formatMessage({id: 'NEWSLETTER.MODAL.SAVE'})}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ── Delete Confirm Modal ── */}
      <Modal show={!!deleteRow} onHide={() => !busyId && setDeleteRow(null)} centered size='sm'>
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title className='fw-bold fs-5'>
            {intl.formatMessage({id: 'NEWSLETTER.MODAL.DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center py-6'>
          <div className='symbol symbol-50px symbol-circle mb-4 mx-auto'>
            <div className='symbol-label bg-light-danger'>
              <KTSVG
                path='/media/icons/duotune/general/gen027.svg'
                className='svg-icon-2x svg-icon-danger'
              />
            </div>
          </div>
          <p className='text-gray-700 mb-0'>
            {intl.formatMessage(
              {id: 'NEWSLETTER.MODAL.DELETE_BODY'},
              {email: <strong>{deleteRow?.email || ''}</strong>},
            )}
          </p>
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0 justify-content-center'>
          <button
            type='button'
            className='btn btn-light'
            onClick={() => setDeleteRow(null)}
            disabled={!!busyId}
          >
            {intl.formatMessage({id: 'NEWSLETTER.MODAL.CANCEL'})}
          </button>
          <button
            type='button'
            className='btn btn-danger'
            disabled={!!busyId}
            onClick={() => void confirmDelete()}
          >
            {intl.formatMessage({id: 'NEWSLETTER.GENERAL.DELETE'})}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {NewslettersList}
